import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { addMonths, isSameDay } from 'date-fns';
import { MAX_NEXT_MONTHS } from 'src/common/constants';
import { getDatesInRange, todayDate } from 'src/common/utils/dateHelpers';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import RadioGroup from 'src/common/components/Radio/RadioGroup';
import RadioButton from 'src/common/components/Radio/RadioButton';
import { DatepickerVariants, HighlightDatesType, type DatepickerProps } from '../Datepicker/types';
import styles from './DatesInputFilter.module.scss';
import Datepicker from '../Datepicker';

const isDate = (date: Date | null) => date instanceof Date;

export type DatesInputFilterProps = {
  dates: Date[];
  dateRangeOptions: {
    value: number;
    labelI18nKey: string;
    onRangeClick: (dates: Date[]) => void;
  }[];
  rangeOption: number;
  showClearButton?: boolean;
  highlightDates?: HighlightDatesType;
  classNameModifier?: DatepickerVariants;
  onChange: (dates: Date[]) => void;
  monthsShown?: number;
} & Pick<DatepickerProps, 'collapseLastRowIfEmpty'>;
const DatesInputFilter: FC<DatesInputFilterProps> = ({
  dates,
  dateRangeOptions,
  rangeOption,
  showClearButton = true,
  highlightDates,
  classNameModifier,
  onChange,
  monthsShown,
  collapseLastRowIfEmpty,
}) => {
  const [t] = useTranslation('search-and-filters');

  const date = dates.find(isDate) ?? null;

  const [minDate, maxDate] = useMemo(() => {
    const today = todayDate();
    return [today, addMonths(today, MAX_NEXT_MONTHS)];
  }, []);

  const getDayClassName = (calDate: Date) => (
    classNames(styles.day, dates.some((d) => isSameDay(calDate, d)) && styles.disabled)
  );

  const handleDatepickerChange = useCallback(
    (newDate: Date) => {
      onChange(getDatesInRange(newDate, Math.max(0, rangeOption)));
    },
    [onChange, rangeOption],
  );

  const handleRadioGroupChange = useCallback((option: string | number) => {
    const dateRangeOption = dateRangeOptions.find(
      (range) => range.value === Number(option),
    );
    if (dateRangeOption) {
      dateRangeOption.onRangeClick(getDatesInRange(date ?? todayDate(), dateRangeOption?.value));
    }
  }, [date, dateRangeOptions]);

  return (
    <div className={getClassNames(styles, 'root', classNameModifier)}>
      <Datepicker
        inline
        minDate={minDate}
        maxDate={maxDate}
        id="search-modal-dates-filter"
        value={date}
        onChange={handleDatepickerChange}
        classNameModifier={classNameModifier}
        dayClassName={getDayClassName}
        highlightDates={highlightDates}
        monthsShown={monthsShown}
        collapseLastRowIfEmpty={collapseLastRowIfEmpty}
      />
      <div className={getClassNames(styles, 'flexibilityWrapper', classNameModifier)}>
        <RadioGroup
          value={date ? rangeOption : 0}
          onChange={handleRadioGroupChange}
        >
          <div className={getClassNames(styles, 'dateRangeWrapper', classNameModifier)}>
            {dateRangeOptions.map((option) => (
              <RadioButton
                key={option.value}
                value={option.value}
                size="s"
                data-test={option.value.toString()}
                variant="primary"
              >
                <span>
                  {option.value > 0 && (
                    <>
                      &plusmn;
                      {' '}
                    </>
                  )}
                  {t(option.labelI18nKey, { count: option.value > 0 ? option.value : undefined })}
                </span>
              </RadioButton>
            ))}
          </div>
        </RadioGroup>
        {showClearButton && (
          <button
            type="button"
            className={styles.clearDates}
            onClick={() => onChange([])}
            disabled={dates.length === 0}
          >
            {t('filter-clear-dates-button')}
          </button>
        )}
      </div>
    </div>
  );
};

export default DatesInputFilter;
